import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Checkbox } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ValidatedDynamicSearchAdSetting } from './validated-dsa-setting';

const getStyles = (_, { palette }) => ({
  campaignDynamicSetting: {
    '& .fui-Checkbox .fui-Label': {
      padding: '0 4px',
    },

    '& .description': {
      marginLeft: 36,

      '&.disabled': {
        color: palette.neutralTertiaryAlt,
      },
    },
  },
});

export const CampaignDynamicSearchAdSetting = ({
  validationProps, updateValidation,
}) => {
  const { data } = validationProps;
  const {
    hasDsaSettings,
    disableDsaSettings,
  } = data || {};

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const activity = useContext(ActivityContext);

  const onDynamicSearchAdsEnabledChange = useCallback((_evt, option) => {
    if (option.selectedOptions !== hasDsaSettings) {
      updateValidation([], {
        hasDsaSettings: option.checked,
      });
      activity.signal(`onHasDSASettingsChanged: ${option.checked}`);
    }
  }, [activity, hasDsaSettings, updateValidation]);

  return (
    <div className={classnames('campaign-dynamic-setting-section', classes.campaignDynamicSetting)}>
      <Checkbox
        className="dsa-switch"
        // specify this as intial undefined value will impact the checkbox state
        checked={hasDsaSettings || false}
        disabled={disableDsaSettings}
        onChange={onDynamicSearchAdsEnabledChange}
        label={i18n.getString(_TL_('Enable Dynamic Search ads'))}
      />
      <p className={classnames('description', disableDsaSettings ? 'disabled' : '')}>
        {i18n.getString(_TL_('By enabling this setting, we\'ll create targeted and relevant ads automatically.'))}
        <LearnMoreLink
          linkText={i18n.getString(_TL_('Learn more'))}
          ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Enable Dynamic Search ads')) })}
          topic="app54695"
        />
      </p>
      {hasDsaSettings && (
        <ValidatedDynamicSearchAdSetting
          data={data}
          isMessageBarVisible={false}
          disabled={disableDsaSettings}
        />
      )}
    </div>
  );
};

CampaignDynamicSearchAdSetting.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      hasDsaSettings: PropTypes.bool,
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
};

export const ValidatedCampaignDynamicSearchAdSetting = withCustomValidation(CampaignDynamicSearchAdSetting);
